import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEntitlements } from '../../store/EntitlementStore';
import { useTheme } from '../../store/ThemeContext';
import api from '../../services/api';
import { startLogout } from '../../utils/session';
import {
  LayoutDashboard,
  Users,
  Folder,
  Mail,
  CheckSquare,
  Calendar,
  CreditCard,
  FileText,
  Settings,
  UserCog,
  Lock,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Palette,
  Menu,
  X,
} from 'lucide-react';
import './PortalrAppLayout.css';

/**
 * Portalr Core App Layout with module-aware navigation.
 */
export const PortalrAppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, hasFeature, hasEntitlement, loading } = useEntitlements();
  const { theme, effectiveTheme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get theme icon
  const getThemeIcon = () => {
    if (theme === 'light') return Sun;
    if (theme === 'dark') return Moon;
    return Monitor; // auto
  };

  const ThemeIcon = getThemeIcon();
  const userInitials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || 'U';

  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';
  const isClient = user?.is_client_user || user?.role === 'client';

  // Navigation items
  const navItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/',
      icon: LayoutDashboard,
    },
    {
      id: 'clients',
      label: 'Clients',
      path: '/clients',
      icon: Users,
      staffOnly: true, // Only staff can see clients management
    },
    {
      id: 'documents',
      label: 'Documents',
      path: '/documents',
      icon: Folder,
    },
    {
      id: 'messages',
      label: 'Messages',
      path: '/messages',
      icon: Mail,
    },
    {
      id: 'tasks',
      label: 'Tasks',
      path: '/tasks',
      icon: CheckSquare,
    },
    {
      id: 'appointments',
      label: 'Appointments',
      path: '/appointments',
      icon: Calendar,
    },
    {
      id: 'invoices',
      label: 'Invoices',
      path: '/invoices',
      icon: CreditCard,
    },
    {
      id: 'tax',
      label: 'Tax',
      path: '/app/tax',
      icon: FileText,
      requiresFeature: 'enable_tax', // Only show if tax is enabled
    },
    {
      id: 'team',
      label: 'Team Management',
      path: '/settings/team',
      icon: UserCog,
      adminOnly: true,
    },
    {
      id: 'branding',
      label: 'Branding',
      path: '/settings/branding',
      icon: Palette,
      adminOnly: true, // Only tenant admin can manage branding
      requiresFeature: 'enable_white_label', // Only show if branding is enabled
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/settings',
      icon: Settings,
    },
  ];

  const handleNavClick = (e, item) => {
    e.preventDefault();
    
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false);
    
    // If locked, navigate to subscriptions page
    if (item.locked) {
      navigate('/settings/subscriptions', { state: { highlightModule: item.id } });
      return;
    }
    
    navigate(item.path);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="app-layout-loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="portalr-app-layout">
      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-backdrop" 
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      
      <aside className={`portalr-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="portalr-sidebar-header">
          <div className="portalr-logoWrap">
            <span className="portalr-logoBadge" aria-hidden="true">M</span>
            <div>
              <h2 className="portalr-logo">
                {hasFeature('enable_white_label') || hasEntitlement('white_label')
                  ? `${tenant?.firm_name || tenant?.name || 'Portalr'} Portal`
                  : 'Portalr'}
              </h2>
              <p className="portalr-logoMeta">Mamba Solutions Control Center</p>
            </div>
          </div>
          <button
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>
        <nav className="portalr-sidebar-nav">
          <ul className="portalr-nav-list">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const isLocked = item.locked;
              const isAdminOnly = item.adminOnly && user?.role !== 'admin';
              const isStaffOnly = item.staffOnly && !isStaff;
              const requiresFeature = item.requiresFeature && !hasFeature(item.requiresFeature);
              const noInvoicePermission =
                item.id === 'invoices' &&
                isStaff &&
                user?.role !== 'admin' &&
                !user?.can_view_invoices;

              // Hide admin-only items from non-admins
              if (isAdminOnly) {
                return null;
              }

              // Hide staff-only items from clients
              if (isStaffOnly) {
                return null;
              }

              // Hide Invoices from staff without can_view_invoices (admins and clients always see it)
              if (noInvoicePermission) {
                return null;
              }

              // Hide items that require a feature that isn't enabled
              if (requiresFeature) {
                return null;
              }
              
              const IconComponent = item.icon;
              return (
                <li key={item.id} className="portalr-nav-item">
                  <a
                    href={item.path}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`portalr-nav-link ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                    title={isLocked ? 'Upgrade to unlock' : item.label}
                  >
                    <span className="portalr-nav-icon">
                      <IconComponent size={20} />
                    </span>
                    <span className="portalr-nav-label">{item.label}</span>
                    {item.badge && (
                      <span className="portalr-nav-badge">{item.badge}</span>
                    )}
                    {isLocked && (
                      <span className="portalr-nav-lock">
                        <Lock size={14} />
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
      
      <div className="portalr-main-content">
        <header className="portalr-header">
          <div className="portalr-header-left">
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
            <h1 className="portalr-page-title">
              {navItems.find(item => item.path === location.pathname)?.label || 'Portalr'}
            </h1>
          </div>
          <div className="portalr-header-right">
            {tenant && (
              <span className="portalr-tenant-name">{tenant.firm_name || tenant.name}</span>
            )}
            <button
              className="btn-theme-toggle"
              onClick={toggleTheme}
              title={`Theme: ${theme === 'auto' ? 'Auto (System)' : theme === 'light' ? 'Light' : 'Dark'}`}
              aria-label="Toggle theme"
            >
              <ThemeIcon size={18} />
            </button>
            {user && (
              <div className="portalr-user-menu">
                <span className="portalr-user-avatar" aria-hidden="true">{userInitials}</span>
                <span className="portalr-user-email">{user.email}</span>
                <button
                  className="btn-logout"
                  // `startLogout` navigates to the BFF's /auth/logout, which
                  // ends the server-side session and then Keycloak's
                  // (MP-MIG-001). What was here cleared two local-storage keys
                  // and redirected — which under the BFF signs nobody out: the
                  // session cookie survives, so the next login completes with
                  // no password and the user believes they have left.
                  onClick={startLogout}
                  title="Logout"
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}
          </div>
        </header>
        
        <main className="portalr-content">
          {children}
        </main>
      </div>
    </div>
  );
};

